import { Product } from '@portal/portal-core';
import { NavigationActions } from 'actions';
import { TOOL_BUTTON_NAMES } from 'constants/user-account';
import { fjButtonTypes } from 'components/primitives/fj-button';
import { BuyButton, DownloadButton, HollowButton, RenewButton } from 'components/product-buttons';
import * as ContactEmails from 'constants/contact-emails';
import {
  ACTIVE_ADMIN,
  ACTIVE_INDIVIDUAL_TRIAL,
  ACTIVE_INSTITUTIONAL_TRIAL,
  ACTIVE_LICENSE,
  EXPIRED_TRIAL,
  EXPIRING_SOON,
  INACTIVE_LICENSE,
  NO_ACTION,
  SUSPENDED,
} from 'constants/product-tab';
import { FLOWJO_DOCS_URL, PRODUCT_INFO } from 'constants/products';
import Lockr from 'utils/local-store';
import React, { Fragment, ReactElement, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import { selectLicenseStatus } from 'selectors';
import * as utils from 'utils';
import ProductSplash from '../product-splash';
import LicenseInfoLink from './license-info-link';
import { getlatestInstallerFromJson } from './installer-json-utils';

import heroImage from '../images/flowjo-11-hero-2-monitor.png';

const { NAMES_UI_TABS, NAMES } = Product;

// Lockr.get returns `unknown` (localStorage boundary); these keys hold installer URL strings.
const flowJoUrlOsx = Lockr.get('flowJoDownloadUrlOsx') as string | undefined;
const flowJoUrlWin = Lockr.get('flowJoDownloadUrlWin') as string | undefined;
const flowJo11UrlWin = Lockr.get('flowJo11DownloadUrlWin') as string | undefined;
const flowJo11UrlMacX64 = Lockr.get('flowJo11DownloadUrlMacX64') as string | undefined;
const flowJo11UrlMacARM64 = Lockr.get('flowJo11DownloadUrlMacARM64') as string | undefined;

const NEW_WINDOW_ATTRS = {
  rel: 'noopener noreferrer',
  target: '_blank',
};

interface FlowJoTabProps {
  marketingContentOnly?: boolean;
}

interface PageContent {
  header: string;
  description: ReactNode;
  buttons: (ReactElement | null)[];
  additionalContent: ReactNode;
}

const FlowJoTab = ({ marketingContentOnly = false }: FlowJoTabProps): ReactElement | null => {
  const { isInstitutional, isAdmin, licenseState } = useSelector(selectLicenseStatus);

  const dispatch = useAppDispatch();
  const openLicenseDrawer = (): void => {
    dispatch(NavigationActions.openLeftDrawerWithTab(TOOL_BUTTON_NAMES.LICENSE));
  };

  const productLinks = {
    trialLink: (
      <a {...NEW_WINDOW_ATTRS} href="./product-trial" title="FlowJo™ v11 Software Trial">
        Sign up for a trial today!
      </a>
    ),
    customerSupportLink: (
      <a href="mailto:techsupport@flowjo.com" title="FlowJo Customer Support">
        Customer Support
      </a>
    ),
    fjUniversityLink: (
      <a
        {...NEW_WINDOW_ATTRS}
        href="https://www.flowjo.com/learn/flowjo-university"
        title="FlowJo University"
      >
        FlowJo University
      </a>
    ),
    newsletterLink: (
      <a {...NEW_WINDOW_ATTRS} href="https://www.flowjo.com/newsletter" title="FlowJo Newsletter">
        Sign Up
      </a>
    ),
    documentationLink: (
      <a {...NEW_WINDOW_ATTRS} href={FLOWJO_DOCS_URL} title="FlowJo Documentation">
        FlowJo Documentation
      </a>
    ),
  };

  // TODO: refactor to leverage /constants/
  const productButtons = (() => {
    const chinaFlag = utils.chinaFlag();
    const freeTrialText = chinaFlag ? '免费试用' : 'Free Trial';
    const downloadTextOsx = chinaFlag ? '下载软件 v10 Mac' : 'Download for Mac';
    const downloadTextWin = chinaFlag ? '下载软件 v10 Windows' : 'Download for Windows';
    const download11TextMacX64 = chinaFlag
      ? '下载软件 v11 Mac (Intel)'
      : 'Download FlowJo 11 for Mac X64';
    const download11TextMacARM64 = chinaFlag
      ? '下载软件 v11 Mac (Apple Silicon)'
      : 'Download FlowJo 11 for Mac ARM64';
    const download11TextWin = chinaFlag ? '下载软件 v11 Windows' : 'Download FlowJo 11 for Windows';
    const demoText = chinaFlag ? '预约演示' : 'Request Demo';
    const worldwideDownloadOSX = flowJoUrlOsx || getlatestInstallerFromJson('flowjo', 'macos');
    const worldwideDownloadWindows =
      flowJoUrlWin || getlatestInstallerFromJson('flowjo', 'windows', 'https://cloud.flowjo.com');
    const flowjoDownloadUrlOsx = chinaFlag ? flowJoUrlOsx : worldwideDownloadOSX;
    const flowjoDownloadUrlWin = chinaFlag ? flowJoUrlWin : worldwideDownloadWindows;
    const buyNowUrl = 'https://www.flowjo.com/solutions/flowjo/request-quote';
    const adminUrl = `/admin?productName=${NAMES_UI_TABS.flowJo}`;
    const freeTrialPath = chinaFlag
      ? `https://bdb-flowa2z.bectondickinson.cn/flowjo/index`
      : `/product-trial?productName=${NAMES.flowJo}`;
    const demoPath = chinaFlag
      ? `https://bdb-flowa2z.bectondickinson.cn/flowjo/index`
      : `mailto:${ContactEmails.getContactEmail(NAMES_UI_TABS.flowJo)}?subject=FlowJo Demo Request`;
    return {
      buyButton: (
        <BuyButton
          product={NAMES_UI_TABS.flowJo}
          buttonType={fjButtonTypes.XL}
          buyNowUrl={buyNowUrl}
          key="buy"
        />
      ),
      renewButton: (
        <RenewButton product={NAMES_UI_TABS.flowJo} buttonType={fjButtonTypes.XL} key="renew" />
      ),
      trialButton: (
        <HollowButton
          {...NEW_WINDOW_ATTRS}
          key="trial"
          href={freeTrialPath}
          text={freeTrialText}
          target={undefined}
        />
      ),
      demoButton: (
        <HollowButton
          {...NEW_WINDOW_ATTRS}
          key="demo"
          href={demoPath}
          text={demoText}
        />
      ),
      downloadButton: (
        <DownloadButton
          {...NEW_WINDOW_ATTRS}
          key="download"
          href="https://flowjo.com/flowjo/download"
          text="Downloads"
        />
      ),
      downloadButtonOsx: (
        <DownloadButton
          {...NEW_WINDOW_ATTRS}
          key="downloadOsx"
          href={flowjoDownloadUrlOsx}
          text={downloadTextOsx}
        />
      ),
      downloadButtonWin: (
        <DownloadButton
          {...NEW_WINDOW_ATTRS}
          key="downloadWin"
          href={flowjoDownloadUrlWin}
          text={downloadTextWin}
        />
      ),
      download11ButtonMacX64: (
        <DownloadButton
          {...NEW_WINDOW_ATTRS}
          key="download11MacX64"
          href={flowJo11UrlMacX64}
          text={download11TextMacX64}
        />
      ),
      download11ButtonMacARM64: (
        <DownloadButton
          {...NEW_WINDOW_ATTRS}
          key="download11MacARM64"
          href={flowJo11UrlMacARM64}
          text={download11TextMacARM64}
        />
      ),
      download11ButtonWin: (
        <DownloadButton
          {...NEW_WINDOW_ATTRS}
          key="download11Win"
          href={flowJo11UrlWin}
          text={download11TextWin}
        />
      ),
      adminButton: <HollowButton key="admin" href={adminUrl} text="Go to Admin" />,
    };
  })();

  const additionalContent = (() => {
    const chinaFlag = utils.chinaFlag();
    const blurb1Header = chinaFlag ? '沉浸式用户体验，操作直观流畅' : 'The Right License For You';
    const blurb1Text = chinaFlag
      ? '采用一体化界面，无需额外插件配置，助你快速、高效地开启分析工作。'
      : 'Explore a wide-range of license options and choose the one that works best for you.';
    const blurb2Header = chinaFlag ? '性能更强劲，结果更迅捷' : 'Native tSNE';
    const blurb2Text = chinaFlag
      ? '算法速度升级，可更快分析更大规模数据集；内置图表、表格与差异统计功能。'
      : 'FlowJo 10.5 and up includes tSNE as a native feature, which is a preferable method of calculating derived parameters due to its supremely improved performance.';
    const blurb3Header = chinaFlag ? '内置实用功能，提升工作流效率' : 'The FlowJo Workspace';
    const blurb3Text = chinaFlag
      ? '集成众多常用算法，提供丰富的分析工具与算法，适用于质控、聚类等多种场景。'
      : 'A powerful statistical environment that is used for immunophenotyping, cell cycle, proliferation, kinetics studies, quantitative population comparison, or plate screening assays to name a few.';

    return {
      blurbs: (
        <Fragment>
          <div>
            <b>{blurb1Header}</b>
            <p>{blurb1Text}</p>
          </div>
          <div>
            <b>{blurb2Header}</b>
            <p>{blurb2Text}</p>
          </div>
          <div>
            <b>{blurb3Header}</b>
            <p>{blurb3Text}</p>
          </div>
        </Fragment>
      ),
      highlights: (
        <Fragment>
          <h1>Introducing FlowJo™ v11 Software. Experience the future of data analysis!</h1>
          <div>
            <b>Immersive user experance for intuitive navigation</b>
            <p>
              Featuring an all-in-one interface that requires no additional plugin setup, allowing
              you to start your analysis quickly and efficiently.
            </p>
          </div>
          <div>
            <b>Faster performance for quicker results</b>
            <p>
              Enhanced algorithm speed for quicker analysis of larger data sets and built-in charts,
              tables and differential statistics.
            </p>
          </div>
          <div>
            <b>Built-in features for added workflow efficiency</b>
            <p>
              Many of your favorite algorithms integrated into the design, offering a wide range of
              anlysis tools and algorithms for QC, clustering, and more.
            </p>
          </div>
        </Fragment>
      ),
    };
  })();

  const getPageContent = (): PageContent | null => {
    const { customerSupportLink, fjUniversityLink } = productLinks;
    const {
      trialButton,
      demoButton,
      downloadButton,
      downloadButtonOsx,
      downloadButtonWin,
      download11ButtonMacARM64,
      download11ButtonMacX64,
      download11ButtonWin,
      adminButton,
    } = productButtons;
    const buyButton = isInstitutional && !isAdmin ? null : productButtons.buyButton;
    const renewButton = isInstitutional && !isAdmin ? null : productButtons.renewButton;
    const { blurbs, highlights } = additionalContent;
    const chinaFlag = utils.chinaFlag();

    if (chinaFlag) {
      return {
        header: '零门槛上手 专家级结果',
        description: <Fragment>FlowJo™ v11你的流式数据全能助手软件</Fragment>,
        buttons: [
          buyButton,
          trialButton,
          demoButton,
          downloadButtonOsx,
          downloadButtonWin,
          download11ButtonMacX64,
          download11ButtonMacARM64,
          download11ButtonWin,
        ],
        additionalContent: blurbs,
      };
    }
    switch (licenseState) {
      case NO_ACTION:
        return {
          header: 'The Leading Platform for Superior Analysis',
          description: (
            <Fragment>
              Take your research wherever you go with an instrument-agnostic platform that accepts
              all FCS file types. Use our intuitive, drag and drop interface to seamlessly run your
              trailblazing analysis in or outside the lab.
            </Fragment>
          ),
          buttons: [buyButton, trialButton, demoButton, downloadButton],
          additionalContent: blurbs,
        };

      case ACTIVE_INDIVIDUAL_TRIAL:
        return {
          header: 'Thank You for Signing Up for a FlowJo™ v11 Software Trial!',
          description: (
            <Fragment>
              We want to help you to enhance your single-cell flow cytometry analysis! You can find
              free FlowJo tutorials and training webinars on {fjUniversityLink}.
            </Fragment>
          ),
          buttons: [buyButton, demoButton, downloadButton],
          additionalContent: highlights,
        };

      case EXPIRED_TRIAL:
        return {
          header: 'Your FlowJo Trial Has Ended!',
          description: (
            <Fragment>
              Your FlowJo trial has expired! Ready to upgrade to a full license? Contact{' '}
              {customerSupportLink} today to discuss licensing options.
            </Fragment>
          ),
          buttons: [buyButton, demoButton, downloadButton],
          additionalContent: blurbs,
        };

      case ACTIVE_LICENSE:
        return {
          header: 'FlowJo Starts Here',
          description: (
            <Fragment>
              Your data analysis just got better. Take a look at our free educational resources via
              the FlowJo University tab to help you excel in FlowJo.
            </Fragment>
          ),
          buttons: [downloadButton],
          additionalContent: highlights,
        };
      case ACTIVE_INSTITUTIONAL_TRIAL:
        return {
          header: 'Your seat is active on a FlowJo trial license!',
          description: (
            <Fragment>
              Your data analysis just got better. Take a look at our free educational resources via
              the FlowJo University tab to help you excel in FlowJo.
            </Fragment>
          ),
          buttons: [downloadButton],
          additionalContent: highlights,
        };

      case ACTIVE_ADMIN:
        return {
          header: 'FlowJo Starts Here',
          description: (
            <Fragment>
              Your data analysis just got better. Take a look at our free educational resources via
              the FlowJo University tab to help you excel in FlowJo.
            </Fragment>
          ),
          buttons: [adminButton, downloadButton],
          additionalContent: highlights,
        };
      case SUSPENDED:
        return {
          header: 'Continue Your Discovery, Contact FlowJo Today!',
          description: (
            <Fragment>
              Your FlowJo license has has been deactivated! Ready to resolve? Contact{' '}
              {customerSupportLink} today to discuss licensing and payment options.
            </Fragment>
          ),
          buttons: [buyButton, demoButton, downloadButton],
          additionalContent: blurbs,
        };
      case INACTIVE_LICENSE:
        return {
          header: 'Continue Your Discovery, Upgrade FlowJo Today!',
          description: (
            <Fragment>
              Your FlowJo license has expired! Ready to renew? Contact {customerSupportLink} today
              to discuss licensing options.
            </Fragment>
          ),
          buttons: [buyButton, demoButton, downloadButton],
          additionalContent: blurbs,
        };
      case EXPIRING_SOON:
        return {
          header: 'FlowJo License Will Expire Soon',
          description: (
            <Fragment>
              Your FlowJo license will expire soon! Ready to renew? Contact {customerSupportLink}{' '}
              today to discuss licensing and payment options.
            </Fragment>
          ),
          buttons: [renewButton, downloadButton],
          additionalContent: blurbs,
        };
      default:
        return null;
    }
  };

  const pageContent = getPageContent();
  if (!pageContent) {
    return null;
  }

  const licenseInfoLink = !marketingContentOnly ? (
    <LicenseInfoLink onClick={openLicenseDrawer} />
  ) : null;

  return (
    <ProductSplash
      header={pageContent.header}
      description={pageContent.description}
      additionalContent={pageContent.additionalContent}
      heroImg={{ src: heroImage, alt: 'flowjo product preview' }}
      buttons={marketingContentOnly ? null : pageContent.buttons}
      productInfo={{
        name: PRODUCT_INFO[NAMES_UI_TABS.flowJo].name,
        // logoSvg is a non-null svg import for FlowJo; coerce the nullable entry type
        logoSvg: PRODUCT_INFO[NAMES_UI_TABS.flowJo].logoSvg ?? '',
      }}
      licenseInfoLink={licenseInfoLink}
    />
  );
};

export default FlowJoTab;
